/**
 * Fiscal-period helpers.
 *
 * A single, dependency-free implementation of fiscal-year and fiscal-quarter
 * boundaries so the forecast engine (server) and the date-filter UI (web) align
 * on the same "Q3" — a forecast quarter must match an invoice-filter quarter.
 *
 * `fiscalYearStartMonth` is 0-indexed (0 = January). The org default is April
 * (3), matching `organization-settings.ts`. All math is native `Date`
 * arithmetic (overflow-safe via the `Date(y, m, d)` constructor) so this stays
 * free of date-fns and usable from every package.
 */

/** Org default fiscal-year start month (0-indexed). 3 = April. */
export const DEFAULT_FISCAL_YEAR_START_MONTH = 3

/** Last millisecond of the given calendar day, in local time. */
const endOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)

/** First day (00:00) of the fiscal year that contains `date`. */
export function startOfFiscalYear(
  date: Date,
  fiscalYearStartMonth: number = DEFAULT_FISCAL_YEAR_START_MONTH,
): Date {
  const fiscalYear =
    date.getMonth() >= fiscalYearStartMonth
      ? date.getFullYear()
      : date.getFullYear() - 1
  return new Date(fiscalYear, fiscalYearStartMonth, 1)
}

/** Last millisecond of the fiscal year that contains `date`. */
export function endOfFiscalYear(
  date: Date,
  fiscalYearStartMonth: number = DEFAULT_FISCAL_YEAR_START_MONTH,
): Date {
  const start = startOfFiscalYear(date, fiscalYearStartMonth)
  // One year later minus one day = the fiscal year's last day.
  return endOfLocalDay(
    new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1),
  )
}

/** First day (00:00) of the fiscal quarter that contains `date`. */
export function startOfFiscalQuarter(
  date: Date,
  fiscalYearStartMonth: number = DEFAULT_FISCAL_YEAR_START_MONTH,
): Date {
  const fiscalYearStart = startOfFiscalYear(date, fiscalYearStartMonth)
  const monthsSinceFiscalYearStart =
    (date.getFullYear() - fiscalYearStart.getFullYear()) * 12 +
    date.getMonth() -
    fiscalYearStart.getMonth()
  const quarterOffset = Math.floor(monthsSinceFiscalYearStart / 3) * 3
  return new Date(
    fiscalYearStart.getFullYear(),
    fiscalYearStart.getMonth() + quarterOffset,
    1,
  )
}

/** Last millisecond of the fiscal quarter that contains `date`. */
export function endOfFiscalQuarter(
  date: Date,
  fiscalYearStartMonth: number = DEFAULT_FISCAL_YEAR_START_MONTH,
): Date {
  const start = startOfFiscalQuarter(date, fiscalYearStartMonth)
  // Three months on, day 0 = the quarter's last day.
  return endOfLocalDay(new Date(start.getFullYear(), start.getMonth() + 3, 0))
}

/** Start of the fiscal quarter immediately after the one containing `date`. */
export function startOfNextFiscalQuarter(
  date: Date,
  fiscalYearStartMonth: number = DEFAULT_FISCAL_YEAR_START_MONTH,
): Date {
  const start = startOfFiscalQuarter(date, fiscalYearStartMonth)
  return new Date(start.getFullYear(), start.getMonth() + 3, 1)
}
